import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { makeStyles } from "@mui/styles";
import { Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import WifiOffIcon from "@mui/icons-material/WifiOff";

import { getCallingBoard } from "apis/orderAPIs";
import {
  BUCKET_READY,
  KIND_PAGER,
  boardNumberScale,
  boardTileTrack,
} from "util/orderStatus";

// A wall display is read across a room, so this screen carries its own dark,
// high-contrast palette rather than the admin theme's light surfaces.
const INK = "#0B0F14";
const INK_RAISED = "#141A22";
const HAIRLINE = "rgba(255, 255, 255, 0.08)";
const MUTED = "rgba(255, 255, 255, 0.45)";
const PREPARING_INK = "#FFB020";
const READY_INK = "#3DDC84";

const REFRESH_MS = 5000;
const CLOCK_MS = 30000;
const RETRIES = 2;

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: INK,
    color: theme.palette.white,
    minHeight: "100vh",
    overflow: "hidden",
    // The app ships no CssBaseline, so plain elements would fall back to the
    // browser's serif default. Everything below is plain elements, not MUI
    // Typography, because a wall display sizes its own text.
    fontFamily: theme.typography.fontFamily,
  },
  header: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(3, 5, 2, 5),
    borderBottom: `1px solid ${HAIRLINE}`,
  },
  venue: {
    fontSize: "clamp(1.25rem, 2.2vw, 2.25rem)",
    fontWeight: 600,
    letterSpacing: "-0.01em",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  clock: {
    fontSize: "clamp(1rem, 1.8vw, 1.75rem)",
    fontWeight: 500,
    color: MUTED,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  columns: {
    flex: 1,
    display: "grid",
    // Even halves: the ready column earns its prominence from tile size and
    // colour, not from taking width off the kitchen's own queue.
    gridTemplateColumns: "1fr 1fr",
    minHeight: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gridTemplateRows: "1fr 1fr",
    },
  },
  column: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    padding: theme.spacing(2, 2.5, 2.5, 2.5),
  },
  columnPreparing: {
    borderRight: `1px solid ${HAIRLINE}`,
    [theme.breakpoints.down("md")]: {
      borderRight: "none",
      borderBottom: `1px solid ${HAIRLINE}`,
    },
  },
  columnReady: {
    // A faint lift so the column customers care about reads as the live one.
    backgroundColor: INK_RAISED,
  },
  columnHeading: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  columnLabel: {
    fontSize: "clamp(0.9rem, 1.4vw, 1.5rem)",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
  },
  labelPreparing: { color: PREPARING_INK },
  labelReady: { color: READY_INK },
  count: {
    fontSize: "clamp(0.8rem, 1vw, 1.1rem)",
    fontWeight: 600,
    color: MUTED,
    fontVariantNumeric: "tabular-nums",
  },
  numbers: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    display: "grid",
    alignContent: "start",
    gap: "clamp(0.5rem, 1.2vw, 1.25rem)",
  },
  tile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.15em",
    borderRadius: 14,
    minWidth: 0,
    // A number must never escape its tile, whatever its length.
    overflow: "hidden",
    // Lets the number below size itself against this tile's own width rather
    // than the viewport, which is what kept the text inside it.
    containerType: "inline-size",
  },
  tileKind: {
    fontSize: "clamp(0.55rem, 0.9vw, 0.95rem)",
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    opacity: 0.6,
    lineHeight: 1,
  },
  tileNumber: {
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: "0.01em",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  tilePreparing: {
    padding: "clamp(0.3rem, 0.6vw, 0.6rem) clamp(0.25rem, 0.45vw, 0.5rem)",
    color: "rgba(255, 255, 255, 0.72)",
    backgroundColor: "rgba(255, 255, 255, 0.04)",
    border: `1px solid ${HAIRLINE}`,
  },
  tileReady: {
    padding: "clamp(0.4rem, 0.8vw, 0.85rem) clamp(0.3rem, 0.5vw, 0.55rem)",
    color: INK,
    backgroundColor: READY_INK,
    // Draws the eye to a number that has just landed without animating layout.
    boxShadow: `0 12px 32px rgba(61, 220, 132, 0.22)`,
    animation: "$callArrive 420ms cubic-bezier(0.16, 1, 0.3, 1)",
  },
  numberPreparing: {
    // Viewport-based fallback, deliberately conservative so four wide glyphs
    // still fit on a browser without container query support.
    fontSize: "clamp(1.4rem, 2.6vw, 2.75rem)",
    "@supports (font-size: 1cqi)": {
      // Width against the tile, height against the screen, whichever runs out
      // first - see boardTileTrack. The kitchen queue reads as the quieter of
      // the two columns, so its number sits a notch under the ready one.
      fontSize: "min(calc(var(--number-scale, 25cqi) * 0.85), var(--number-max, 6vh))",
    },
  },
  numberReady: {
    // Lower than it looks: the fallback is sized against the viewport, and the
    // ready column is now half the board rather than the wider share, so this
    // has to clear the narrower tile it lands in.
    fontSize: "clamp(1.75rem, 3.4vw, 3.75rem)",
    "@supports (font-size: 1cqi)": {
      fontSize: "min(var(--number-scale, 25cqi), var(--number-max, 6.8vh))",
    },
  },
  "@keyframes callArrive": {
    from: { opacity: 0, transform: "scale(0.94)" },
    to: { opacity: 1, transform: "scale(1)" },
  },
  empty: {
    color: MUTED,
    fontSize: "clamp(0.95rem, 1.4vw, 1.4rem)",
    fontWeight: 500,
  },
  centred: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    color: MUTED,
    textAlign: "center",
    padding: theme.spacing(4),
  },
  banner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.warning.dark,
    color: theme.palette.white,
    fontWeight: 600,
    fontSize: "clamp(0.8rem, 1.1vw, 1.1rem)",
  },
}));

const formatClock = (date) =>
  date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const NumberColumn = ({ variant, label, entries }) => {
  const classes = useStyles();
  const isReady = variant === BUCKET_READY;
  // Fewer numbers means bigger numbers, so the board is legible from the door.
  const track = boardTileTrack(entries.length, isReady);

  return (
    <section
      className={`${classes.column} ${isReady ? classes.columnReady : classes.columnPreparing}`}
      aria-labelledby={`calling-${variant}`}
    >
      <header className={classes.columnHeading}>
        <span
          className={`${classes.columnLabel} ${isReady ? classes.labelReady : classes.labelPreparing}`}
          id={`calling-${variant}`}
        >
          {label}
        </span>
        {entries.length > 0 && <span className={classes.count}>{entries.length}</span>}
      </header>

      {entries.length === 0 ? (
        <p className={classes.empty}>
          {isReady ? "Nothing waiting to be collected" : "No orders in the kitchen"}
        </p>
      ) : (
        <ul
          className={classes.numbers}
          // The ready column changes under the customer's eye; announce it.
          aria-live={isReady ? "polite" : "off"}
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(${track.minTrack}, 1fr))`,
            gridAutoRows: `minmax(${track.minRow}, auto)`,
          }}
        >
          {entries.map((entry) => (
            <li
              className={`${classes.tile} ${isReady ? classes.tileReady : classes.tilePreparing}`}
              key={`${entry.kind}:${entry.ref}`}
            >
              {/* An order reference carries no label - on a wall display the
                  number is the whole message, and the word above it only ate
                  height the number could use. A pager number keeps its label,
                  because pager 12 and order 12 are two different calls. */}
              {entry.kind === KIND_PAGER && <span className={classes.tileKind}>Pager</span>}
              <span
                className={`${classes.tileNumber} ${
                  isReady ? classes.numberReady : classes.numberPreparing
                }`}
                style={{
                  "--number-scale": boardNumberScale(entry.ref),
                  // Per tile, not per column: a pager tile spends part of its
                  // row on the label above the number, so only it takes the
                  // lower ceiling. An order reference beside it keeps its own.
                  "--number-max":
                    entry.kind === KIND_PAGER ? track.maxLabelledNumber : track.maxNumber,
                }}
              >
                {entry.ref}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

/**
 * Customer-facing order calling board.
 *
 * Public by design: this runs unattended on a screen in the shop, so it never
 * requires a login and the endpoint behind it returns order numbers only.
 */
NumberColumn.propTypes = {
  variant: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      ref: PropTypes.string.isRequired,
      kind: PropTypes.string,
      bucket: PropTypes.string,
      updatedAt: PropTypes.number,
    })
  ).isRequired,
};

const CallingBoard = (props) => {
  const classes = useStyles();
  const restaurantId = props.match.params.restaurantId;

  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), CLOCK_MS);
    return () => clearInterval(timer);
  }, []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["callingBoard", restaurantId],
    queryFn: () => getCallingBoard(restaurantId),
    enabled: Boolean(restaurantId),
    refetchInterval: REFRESH_MS,
    // An unattended screen must keep polling even when the browser thinks the
    // tab is in the background.
    refetchIntervalInBackground: true,
    // Keep the last good board on screen through a blip rather than flashing.
    placeholderData: (previous) => previous,
    // Bounded, not infinite: retrying forever would leave the screen stuck on a
    // spinner. The poll above is what recovers the board once the server is back.
    retry: RETRIES,
    gcTime: Infinity,
  });

  const orders = (data && data.orders) || [];
  const ready = orders.filter((entry) => entry.bucket === BUCKET_READY);
  const preparing = orders.filter((entry) => entry.bucket !== BUCKET_READY);
  const venue = (data && data.restaurant && (data.restaurant.name || data.restaurant.altName)) || "";

  // Numbers on screen, but the latest poll (and its retries) failed: say so
  // rather than let customers trust a board that has stopped moving.
  const isStale = isError && Boolean(data);

  if (isLoading && !data) {
    return (
      <div className={classes.root}>
        <div className={classes.centred}>
          <CircularProgress color="inherit" />
        </div>
      </div>
    );
  }

  if (isError && !data) {
    return (
      <div className={classes.root}>
        <div className={classes.centred}>
          <WifiOffIcon fontSize="large" />
          <Typography variant="h4" color="inherit">
            Order board unavailable
          </Typography>
          <Typography variant="body1" color="inherit">
            Please ask our staff about your order.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {isStale && (
        <div className={classes.banner}>
          <WifiOffIcon fontSize="small" />
          Reconnecting — numbers may be out of date
        </div>
      )}

      <header className={classes.header}>
        <h1 className={classes.venue}>{venue}</h1>
        <span className={classes.clock}>{formatClock(now)}</span>
      </header>

      <div className={classes.columns}>
        <NumberColumn variant="preparing" label="Preparing" entries={preparing} />
        <NumberColumn variant={BUCKET_READY} label="Ready for pickup" entries={ready} />
      </div>
    </div>
  );
};

CallingBoard.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      restaurantId: PropTypes.string,
    }),
  }).isRequired,
};

export default CallingBoard;
